import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Minimize2, MoreVertical, Trash2 } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import { chatbotAPI } from '../../services/chatbotService';
import { logError, logWarning } from '../../utils/errorLogger';
import { IMAGE_URLS } from '../../constants/images';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: string;
}

const CHATBOT_STORAGE_KEY = 'chatbot_messages';
const CHATBOT_CONVERSATION_KEY = 'chatbot_conversation_id';

// Support phone number - must match backend SUPPORT_PHONE setting
// This is a fallback in case backend doesn't detect human support request
const SUPPORT_PHONE = process.env.REACT_APP_SUPPORT_PHONE || '+962XXXXXXXXX';

// Keywords that indicate user wants human support
const HUMAN_SUPPORT_KEYWORDS = [
  'talk to human',
  'speak with person',
  'human help',
  'contact support',
  'talk to someone',
  'customer service',
  'real person',
  'support team',
  'speak to human',
  'human representative',
  'talk to agent',
  'live agent',
  'human agent',
  'can\'t help',
  'not helping',
  'frustrated',
  'escalate',
  'manager',
  'supervisor'
];

// Check if message indicates user wants human support
const wantsHumanSupport = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return HUMAN_SUPPORT_KEYWORDS.some(keyword => lowerMessage.includes(keyword));
};

const ChatBot: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  
  // Load conversation ID from localStorage on mount
  const getInitialConversationId = (): string | undefined => {
    try {
      const saved = localStorage.getItem(CHATBOT_CONVERSATION_KEY);
      return saved || undefined;
    } catch (error) {
      logError('Error loading conversation ID from localStorage', error);
      return undefined;
    }
  };
  
  const [conversationId, setConversationId] = useState<string | undefined>(getInitialConversationId);
  
  // Generate welcome message based on user roles
  const getWelcomeMessage = (): string => {
    if (!user) {
      return "Hello! I'm here to help you with your events. Please log in to access all features.";
    }

    // Get all roles (from roles array or fallback to primary role)
    const allRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];
    const hasAdmin = allRoles.includes('admin');
    const hasHost = allRoles.includes('host');
    const hasGuest = allRoles.includes('guest');
    const hasUsher = allRoles.includes('usher');

    let message = "Hello! I'm your **Event Assistant**. I can help you with:\n\n";

    if (hasAdmin) {
      message += "👑 **Admin Features:**\n\n";
      message += "- View all events and users in the system\n";
      message += "- Get system-wide statistics\n";
      message += "- Search for events by host name\n";
      message += "- Filter users by role (admin, host, guest, usher)\n\n";
    }

    if (hasHost) {
      message += "🏠 **Host Features:**\n\n";
      message += "- View your events and filter by status\n";
      message += "- Check guest lists and response status\n";
      message += "- Get dashboard statistics for your events\n";
      message += "- Search events by name\n\n";
    }

    if (hasGuest) {
      message += "👤 **Guest Features:**\n\n";
      message += "- View your event invitations\n";
      message += "- Check invitation status (pending, accepted, rejected)\n";
      message += "- Get details about specific events you're invited to\n\n";
    }

    if (hasUsher) {
      message += "🎫 **Usher Features:**\n\n";
      message += "- Access usher-specific features\n\n";
    }

    message += "Ask me anything about your events!";

    return message;
  };
  
  // Initialize messages from localStorage or use default welcome message
  const getInitialMessages = (): Message[] => {
    try {
      const saved = localStorage.getItem(CHATBOT_STORAGE_KEY);
      if (saved) {
        const parsedMessages = JSON.parse(saved);
        // Validate that it's an array with at least one message
        if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
          return parsedMessages;
        }
      }
    } catch (error) {
      logError('Error loading chatbot messages from localStorage', error);
    }
    // Default welcome message if no saved messages
    return [
      {
        id: '1',
        text: getWelcomeMessage(),
        isBot: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ];
  };

  const [messages, setMessages] = useState<Message[]>(getInitialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuDropdownRef = useRef<HTMLDivElement>(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(CHATBOT_STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      logError('Error saving chatbot messages to localStorage', error);
      // Handle quota exceeded error
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        logWarning('localStorage quota exceeded. Clearing old messages.');
        // Keep only the last 50 messages
        const recentMessages = messages.slice(-50);
        try {
          localStorage.setItem(CHATBOT_STORAGE_KEY, JSON.stringify(recentMessages));
          setMessages(recentMessages);
        } catch (e) {
          logError('Failed to save reduced message history', e);
        }
      }
    }
  }, [messages]);

  // Save conversation ID to localStorage whenever it changes
  useEffect(() => {
    if (conversationId) {
      try {
        localStorage.setItem(CHATBOT_CONVERSATION_KEY, conversationId);
      } catch (error) {
        logError('Error saving conversation ID to localStorage', error);
      }
    }
  }, [conversationId]);

  // Show greeting message after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGreeting(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const handleSendMessage = async (message: string) => {
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: message,
      isBot: false,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);

    // Frontend detection as backup (backend should handle this primarily)
    if (wantsHumanSupport(message)) {
      const supportResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: `I understand you'd like to speak with a human representative. You can reach our support team directly at:\n\n**Phone: ${SUPPORT_PHONE}**\n\nOur support team is available to assist you with any questions or issues. Please call this number for personalized assistance.`,
        isBot: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, supportResponse]);
      setIsTyping(false);
      return;
    }

    try {
      // Call chatbot API
      const response = await chatbotAPI.sendMessage(message, conversationId);
      
      // Update conversation ID if received
      if (response.conversation_id && response.conversation_id !== conversationId) {
        setConversationId(response.conversation_id);
      }

      // Add bot response
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: response.response,
        isBot: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    } catch (error: any) {
      // Handle errors
      let errorMessage = "Sorry, I'm having trouble connecting. Please try again later.";
      
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 401) {
          errorMessage = "Please log in to use the chatbot.";
        } else if (status === 400) {
          errorMessage = errorData?.error?.message || "Invalid request. Please check your message and try again.";
        } else if (status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (errorData?.error?.message) {
          errorMessage = errorData.error.message;
        }
      } else if (error.message) {
        // Network error or other error
        if (error.message.includes('Network') || error.message.includes('network')) {
          errorMessage = "Connection error. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      // Add error message to chat
      const errorResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: errorMessage,
        isBot: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, errorResponse]);
      setIsTyping(false);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setShowGreeting(false); // Hide greeting when opening chat
    }
  };

  const handleMinimize = () => {
    setIsOpen(false);
    setShowMenu(false); // Close menu when minimizing
  };

  const handleClearChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear localStorage
    try {
      localStorage.removeItem(CHATBOT_STORAGE_KEY);
      localStorage.removeItem(CHATBOT_CONVERSATION_KEY);
    } catch (error) {
      logError('Error clearing chatbot data from localStorage', error);
    }
    
    // Reset conversation ID
    setConversationId(undefined);
    
    // Reset to default welcome message
    const defaultMessage: Message = {
      id: Date.now().toString(),
      text: getWelcomeMessage(),
      isBot: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    
    setMessages([defaultMessage]);
    setShowMenu(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuButtonRef.current &&
        !menuButtonRef.current.contains(target) &&
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(target)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <>
      {/* Chat Button */}
      <div className="fixed bottom-10 sm:bottom-10 right-2 sm:right-4 z-50">
        <div className="relative group">
          <button
            onClick={handleToggle}
            className={`relative h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-cover shadow-lg border-2 transition-all duration-300 group-hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
            title="Chat with us"
          >
            <img
              src={IMAGE_URLS.LANDING_AGENT_ILLUSTRATION}
              alt="Chat Assistant"
              className="h-full w-full rounded-xl object-cover"
            />
            {/* Green status indicator */}
            <div
              className={`absolute -top-1 -right-1 h-3 w-3 sm:h-4 sm:w-4 bg-gradient-to-br from-[#33FF00] to-[#2BD400] rounded-full border-2 shadow-sm ${
                theme === 'dark' ? 'border-gray-900' : 'border-white'
              }`}
            ></div>
            {/* Chat icon overlay */}
            <div
              className={`absolute bottom-0 right-0 h-4 w-4 sm:h-5 sm:w-5 rounded-full flex items-center justify-center ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } shadow-md`}
            >
              <MessageCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-500" />
            </div>
          </button>

          {/* Greeting Tooltip */}
          {showGreeting && !isOpen && (
            <div
              className={`hidden sm:block absolute bottom-full right-0 mb-2 px-3 py-2 rounded-lg shadow-lg whitespace-nowrap animate-fade-in ${
                theme === 'dark'
                  ? 'bg-gray-800 text-white border border-gray-700'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              <p className="text-sm font-medium">Ask me anything.</p>
              {/* Arrow */}
              <div
                className={`absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                  theme === 'dark' ? 'border-t-gray-800' : 'border-t-white'
                }`}
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div
          ref={chatWindowRef}
          className={`fixed bottom-16 right-2 left-2 sm:left-auto sm:bottom-20 sm:right-4 w-auto sm:w-[450px] h-[500px] sm:h-[550px] max-h-[calc(100vh-5rem)] sm:max-h-[calc(100vh-6rem)] rounded-lg shadow-2xl border-2 flex flex-col z-50 animate-slide-up ${
            theme === 'dark'
              ? 'bg-gray-900 border-gray-700'
              : 'bg-white border-gray-200'
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className="relative h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0">
                <img
                  src={IMAGE_URLS.LANDING_AGENT_ILLUSTRATION}
                  alt="Chat Assistant"
                  className="h-full w-full rounded-lg object-cover"
                />
                <div
                  className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 sm:h-3 sm:w-3 bg-gradient-to-br from-[#33FF00] to-[#2BD400] rounded-full border ${
                    theme === 'dark' ? 'border-gray-900' : 'border-white'
                  }`}
                ></div>
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  className={`text-xs sm:text-sm font-semibold truncate ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Event Assistant
                </h3>
                <p
                  className={`text-[10px] sm:text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Online
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0">
              {/* Menu Button */}
              <div className="relative">
                <button
                  ref={menuButtonRef}
                  onClick={() => setShowMenu(!showMenu)}
                  className={`p-1 sm:p-1.5 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  } ${showMenu ? (theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100') : ''}`}
                  title="More options"
                >
                  <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>

                {/* Dropdown Menu */}
                {showMenu && (
                  <div
                    ref={menuDropdownRef}
                    className={`absolute right-0 top-full mt-1 w-40 sm:w-48 rounded-lg shadow-lg border z-50 ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={handleClearChat}
                      className={`w-full text-left px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm flex items-center space-x-2 transition-colors ${
                        theme === 'dark'
                          ? 'text-red-400 hover:bg-gray-700 hover:text-red-300'
                          : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                      }`}
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span>Clear chat</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Minimize Button */}
              <button
                onClick={handleMinimize}
                className={`p-1 sm:p-1.5 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Minimize"
              >
                <Minimize2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div
            className={`flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 min-h-0 ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}
          >
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message.text}
                isBot={message.isBot}
                timestamp={message.timestamp}
              />
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start mb-3 sm:mb-4">
                <div
                  className={`max-w-[80%] rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                  }`}
                >
                  <div className="flex space-x-1">
                    <div
                      className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${
                        theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                      } animate-bounce`}
                      style={{ animationDelay: '0ms' }}
                    ></div>
                    <div
                      className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${
                        theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                      } animate-bounce`}
                      style={{ animationDelay: '150ms' }}
                    ></div>
                    <div
                      className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${
                        theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                      } animate-bounce`}
                      style={{ animationDelay: '300ms' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <ChatInput onSendMessage={handleSendMessage} disabled={isTyping} />
        </div>
      )}

      {/* Add custom animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ChatBot;

